import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import PhoneInput from 'react-phone-input-2'
import 'react-phone-input-2/lib/style.css'
import { Button, Card, CardContent, CardHeader, Input, Label } from '../components/ui'
import { useAuth } from '../hooks/useAuth'
import { CheckCircle, Building2, ArrowLeft } from 'lucide-react'

interface InvitationDetails {
  id: string
  team_name: string
  inviter_name: string
  role: string
  contact: string
  contact_type: 'email' | 'phone'
  expires_at: number
}

export default function InvitationLogin() {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const { user, loading: authLoading } = useAuth()
  
  const [method, setMethod] = useState<'phone' | 'sso' | 'social'>('social')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [loadingProvider, setLoadingProvider] = useState<string | null>(null)
  const [invitation, setInvitation] = useState<InvitationDetails | null>(null)
  const [invitationLoading, setInvitationLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const { login } = useAuth()

  // Redirect if already authenticated
  useEffect(() => {
    if (!authLoading && user) {
      handleAcceptInvitation()
    }
  }, [user, authLoading])

  // Load invitation details
  useEffect(() => {
    if (token) {
      loadInvitationDetails()
    }
  }, [token])

  const loadInvitationDetails = async () => {
    if (!token) return

    try {
      setInvitationLoading(true)
      setError(null)
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/v1/invitations/${token}`
      )

      if (response.ok) {
        const data = await response.json()
        setInvitation(data.invitation)
        
        // Pre-fill contact info if available
        if (data.invitation.contact_type === 'email') {
          setEmail(data.invitation.contact)
        } else if (data.invitation.contact_type === 'phone') {
          setPhoneNumber(data.invitation.contact)
          setMethod('phone') // Default to phone if invitation was sent via phone
        }
      } else if (response.status === 404) {
        setError('This invitation is invalid or has expired')
      } else {
        setError('Failed to load invitation details')
      }
    } catch (err) {
      console.error('Failed to load invitation:', err)
      setError('Failed to load invitation details')
    } finally {
      setInvitationLoading(false)
    }
  }

  const handleAcceptInvitation = async () => {
    if (!token || !user) return

    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/v1/invitations/${token}/accept`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          }
        }
      )

      if (response.ok) {
        // Successful invitation acceptance
        navigate('/dashboard?invitation_accepted=true')
        window.location.reload() // Refresh to update company context
      } else {
        const errorData = await response.json()
        console.error('Failed to accept invitation:', errorData)
        navigate('/dashboard?invite_error=true')
      }
    } catch (err) {
      console.error('Error accepting invitation:', err)
      navigate('/dashboard?invite_error=true')
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError(null)
    
    try {
      if (method === 'phone') {
        await login({ phoneNumber, method: 'phone' })
        // Redirect to invitation-specific OTP verification page
        const otpUrl = `/invite/${token}/verify-otp?phone=${encodeURIComponent(phoneNumber)}&invite=${token}`
        window.location.href = otpUrl
      } else if (method === 'sso') {
        await login({ email, method: 'sso', provider: 'enterprise', inviteToken: token })
      }
    } catch (error) {
      console.error('Login failed:', error)
      setError('Login failed. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const isExpired = invitation && invitation.expires_at < Date.now() / 1000

  if (authLoading || invitationLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading invitation...</p>
        </div>
      </div>
    )
  }

  if (error || !invitation) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
        <div className="max-w-md w-full">
          <Card>
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Building2 className="w-8 h-8 text-red-600" />
              </div>
              
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Invitation Not Found
              </h2>
              
              <p className="text-gray-600 mb-6">
                {error || 'This invitation link is invalid or has expired.'}
              </p>
              
              <Button
                onClick={() => navigate('/login')}
                className="w-full"
                variant="primary"
              >
                Go to Login
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  if (isExpired) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
        <div className="max-w-md w-full">
          <Card>
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Building2 className="w-8 h-8 text-yellow-600" />
              </div>
              
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Invitation Expired
              </h2>
              
              <p className="text-gray-600 mb-6">
                This invitation to join <strong>{invitation.team_name}</strong> has expired.
                Please contact {invitation.inviter_name} for a new invitation.
              </p>
              
              <Button
                onClick={() => navigate('/login')}
                className="w-full"
                variant="primary"
              >
                Go to Login
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Header with company branding area */}
        <div className="text-center">
          <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Building2 className="w-10 h-10 text-blue-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900">Join {invitation.team_name}</h1>
          <p className="mt-2 text-sm text-gray-600">
            {invitation.inviter_name} has invited you to join their team
          </p>
        </div>

        {/* Invitation Details Card */}
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center mb-3">
              <CheckCircle className="w-5 h-5 text-blue-600 mr-2" />
              <span className="font-medium text-blue-900">Team Invitation</span>
            </div>
            <div className="text-sm text-blue-800 space-y-1">
              <div className="flex justify-between">
                <span>Company:</span>
                <span className="font-medium">{invitation.team_name}</span>
              </div>
              <div className="flex justify-between">
                <span>Role:</span>
                <span className="font-medium">{invitation.role}</span>
              </div>
              <div className="flex justify-between">
                <span>Invited by:</span>
                <span className="font-medium">{invitation.inviter_name}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Login Methods Card */}
        <Card>
          <CardHeader>
            <h2 className="text-xl font-semibold text-center text-gray-900">
              Sign in to accept invitation
            </h2>
            <div className="flex space-x-2 mt-4">
              <Button
                variant={method === 'social' ? 'primary' : 'ghost'}
                onClick={() => setMethod('social')}
                className="flex-1 text-sm"
              >
                Social Login
              </Button>
              <Button
                variant={method === 'phone' ? 'primary' : 'ghost'}
                onClick={() => setMethod('phone')}
                className="flex-1 text-sm"
              >
                Phone
              </Button>
              <Button
                variant={method === 'sso' ? 'primary' : 'ghost'}
                onClick={() => setMethod('sso')}
                className="flex-1 text-sm"
              >
                Enterprise SSO
              </Button>
            </div>
          </CardHeader>

          <CardContent>
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                {error}
              </div>
            )}

            {method === 'social' ? (
              <div className="space-y-3">
                <Button
                  type="button"
                  variant="secondary"
                  className="w-full flex items-center justify-center gap-3"
                  onClick={async () => {
                    setLoadingProvider('google')
                    try {
                      await login({ method: 'sso', provider: 'google', inviteToken: token })
                    } catch (error) {
                      console.error('Google login failed:', error)
                      setError('Google login failed. Please try again.')
                    } finally {
                      setLoadingProvider(null)
                    }
                  }}
                  disabled={loadingProvider !== null}
                >
                  {loadingProvider === 'google' ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-900"></div>
                  ) : (
                    <>
                      <svg className="w-5 h-5" viewBox="0 0 24 24">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                      Continue with Google
                    </>
                  )}
                </Button>

                <Button
                  type="button"
                  variant="secondary"
                  className="w-full flex items-center justify-center gap-3"
                  onClick={async () => {
                    setLoadingProvider('microsoft')
                    try {
                      await login({ method: 'sso', provider: 'microsoft', inviteToken: token })
                    } catch (error) {
                      console.error('Microsoft login failed:', error)
                      setError('Microsoft login failed. Please try again.')
                    } finally {
                      setLoadingProvider(null)
                    }
                  }}
                  disabled={loadingProvider !== null}
                >
                  {loadingProvider === 'microsoft' ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-900"></div>
                  ) : (
                    <>
                      <svg className="w-5 h-5" viewBox="0 0 23 23">
                        <path fill="#f25022" d="M1 1h10v10H1z"/>
                        <path fill="#00a4ef" d="M12 1h10v10H12z"/>
                        <path fill="#7fba00" d="M1 12h10v10H1z"/>
                        <path fill="#ffb900" d="M12 12h10v10H12z"/>
                      </svg>
                      Continue with Microsoft
                    </>
                  )}
                </Button>

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300" />
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-white text-gray-500">Or use another method</span>
                  </div>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {method === 'phone' ? (
                  <div>
                    <Label htmlFor="phone">Phone Number</Label>
                    <div className="mt-1">
                      <PhoneInput
                        country={'us'}
                        value={phoneNumber}
                        onChange={(phone) => setPhoneNumber(phone)}
                        inputClass="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2"
                        containerClass="w-full"
                        buttonClass="border border-gray-300 rounded-l-md"
                      />
                    </div>
                    <p className="mt-2 text-sm text-gray-500">
                      We'll send you a verification code via SMS
                    </p>
                  </div>
                ) : (
                  <div>
                    <Label htmlFor="email">Work Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your work email"
                      required
                    />
                    <p className="mt-2 text-sm text-gray-500">
                      Sign in with your organization's SSO
                    </p>
                  </div>
                )}

                <Button
                  type="submit"
                  className="w-full"
                  disabled={loading || (method === 'phone' && !phoneNumber) || (method === 'sso' && !email)}
                >
                  {loading ? 'Signing in...' : method === 'phone' ? 'Send Code' : 'Continue with SSO'}
                </Button>
              </form>
            )}

            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600 mb-4">
                New users will be automatically registered during sign in
              </p>
              
              <button
                onClick={() => navigate(`/invite/${token}`)}
                className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Back to invitation details
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}